import React, {useEffect, useState, useCallback} from 'react';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import {useUserState} from 'contexts/user';
import {usePhaseState} from 'funnel-schedule/phase-context';
import {Container, Col, Row, Button} from 'reactstrap';
import classNames from 'classnames';
import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import Section, {SectionImage} from 'components/Elements/Section';
import Webinar, {WebinarNew} from 'components/Webinar';
import Video from 'components/Video';
import SalesCopy, {SalesCopyV1} from 'components/SalesCopy';
import CheckoutButton from 'components/CheckoutButton';
import Footer from 'components/Elements/Footer';
import RegistrationModal from 'components/RegistrationModal';
import ModalExitScreening from 'components/Modals/ModalExitScreening';
import {SalesDisclaimer} from 'components/SalesCta';
import track from 'utils/track';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';
import SectionCL from 'components/ComponentLibrary/SectionCL';

const COMPLETED_MASTERCLASS_EVENT = 'Completed Watching HHC Masterclass';

export default function ScreeningIndex({replayOverride = false, replayFull = false, variant = 'default', ...props}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  const {phase} = webinarSchedule;
  const {bucket, events = []} = useUserState();
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  const hasCompletedMasterclass = events.includes(COMPLETED_MASTERCLASS_EVENT);
  const beforeWebinar = phase === 'before' && !replayOverride && !replayFull;
  const afterWebinar = (phase === 'after' && !replayOverride) || (phase === 'replay' && !replayOverride);
  const delayReveal = (hasCompletedMasterclass && !replayOverride && phase != 'before') || afterWebinar || replayFull;
  const registrationModalDisabled = replayOverride || replayFull;

  const workbookDownloadUrl = 'https://cdn.foodrevolution.org/hhc/Healthy%20Heart%20Masterclass%20Workbook.pdf';

  useEffect(() => {
    if (hasCompletedMasterclass)
      track('Product Viewed', {
        product_id: 'C-HHC',
        sku: 'C-HHC',
        name: 'Healthy Heart',
        variant: 'hhc-masterclass',
        value: 247,
        currency: 'usd'
      });
  }, [hasCompletedMasterclass]);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes(COMPLETED_MASTERCLASS_EVENT)) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#join') {
        try{
          document.getElementById('watch-cta-thankyou').scrollIntoView();
        }catch(e){
          console.error(e);
        }
      }
    }
  }, [events, anchorScrolled]);

  const productId = 'C-HHC';
  const productSku = 'C-HHC';
  const productName = 'Healthy Heart';
  const productSlug = 'hhc-masterclass';
  const productValue = 247;
  const productPaymentPlanValue = 89;
  const productFullPrice = 497;

  const webinarHeading = (
    <Row>
      <Col>
        <h4 className="section-heading text-center mb-1">Healthy Heart Masterclass</h4>
        <h4 className="text-center mt-0 mb-1">
          <em>4 Heart Myths & 10 Groundbreaking Truths</em>
        </h4>
        <h5 className="text-400 text-center mt-0 mb-4">with Dr. Mimi Guarneri & Ocean Robbins</h5>
      </Col>
    </Row>
  );

  const webinarFooter = (
    <Row>
      <Col>
        <h4 className="text-center text-600 mb-0">
          <a href={workbookDownloadUrl} className="workbook-download text-underline" target="_blank">
            Download your FREE workbook here.
          </a>
        </h4>
        <p className="balance-text text-center mt-4">
          {
            {
              prevent: (
                <>
                  IMPORTANT: Taking the time to keep your heart healthy is the most valuable thing you can do today.
                  You’ll be so glad you did!
                </>
              ),
              optimize: (
                <>
                  IMPORTANT: Taking the time to get the truth about what makes your heart strong is the most valuable
                  thing you can do today. You’ll be so glad you did!
                </>
              ),
              reverse: (
                <>
                  IMPORTANT: Taking the time to keep your heart healthy is the most valuable thing you can do today.
                  You’ll be so glad you did!
                </>
              )
            }[bucket]
          }
        </p>
      </Col>
    </Row>
  );

  function SalesCta({headline, btnText = 'Buy Now', className = undefined, noImage = false, trees = true}) {
    return (
      <div className={classNames(`cta-sales`, className)}>
        {headline && <div className="cta-headline text-center text-white">{headline}</div>}
        <div
          className={classNames(
            `offer-box`,
            `offer-box-shadow`,
            `px-3`,
            `py-4`,
            `px-sm-4`,
            `px-md-5`,
            `py-md-5`,
            `text-center`,
            `background-white`,
            `rounded`
          )}
        >
          {!noImage && (
            <Row className="justify-content-center mb-4">
              <Col xs="10" md="8" xl="7">
                <img
                  src="https://cdn.foodrevolution.org/hhc/hhc-product-image-v2b.png"
                  width="1200"
                  height="667"
                  alt="Healthy Heart product graphic"
                />
              </Col>
            </Row>
          )}
          <h3 className="mt-0 mb-2">
            Regular Price: <del>${productFullPrice}</del>
          </h3>
          <h2 className="text-green mt-0">
            Enroll TODAY for just $247 + Get the Heart Health Collection for FREE! (a $97 value)
          </h2>
          <CheckoutButton className="offer-box-button" sku={productSku} price={productValue} slug={productSlug}>
            <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> {btnText}
          </CheckoutButton>
          <SalesDisclaimer />
          <p className="mt-4 mb-1">Prefer a 3-month payment plan? We’ve got you covered!</p>
          <CheckoutButton
            color="link"
            className="btn-link-checkout"
            sku={productSku}
            price={productPaymentPlanValue}
            slug={productSlug}
            queryParams={{opt: 1}}
          >
            Join now for just ${productPaymentPlanValue}/month!
          </CheckoutButton>
        </div>

        {trees && (
          <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
            <Col xs="11" lg="8" className="mb-4 mb-lg-0">
              <h6 className="section-only-heading text-center text-lg-left text-400">
                For every new order, we make a donation to Trees for the Future, enabling them to plant another organic
                fruit or nut tree in a low-income community.
              </h6>
            </Col>
            <Col xs="11" lg="3" className="text-center">
              <img
                alt="Trees for the Future logo"
                src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                width={1080}
                height={442}
                style={{maxHeight: '100px', width: 'auto'}}
              />
            </Col>
          </Row>
        )}
      </div>
    );
  }

  function CtaOffer({iconColor = 'green', btnText = 'Buy Now'}) {
    return (
      <>
        <img
          className="mb-3"
          src="https://cdn.foodrevolution.org/hhc/hhc-product-image-v2b.png"
          width="1200"
          height="667"
          alt="Healthy Heart product graphic"
          style={{maxHeight: '400px', width: 'auto'}}
        />
        <p>
          Join Dr. Mimi Guarneri and Ocean Robbins for a <b>transformative six-module course</b> that delivers
          science-backed insights and practical tools to <b>help you take control</b> of your heart health,{' '}
          <b>optimize</b> your energy, and <b>build</b> lasting, heart-healthy habits — <b>for life.</b>
        </p>
        <h3 className="section-heading mb-2">
          Regular price: <del>$497</del>
        </h3>
        <h2 className="text-h1 text-green mt-0 mb-3">Now Only: $247</h2>
        <p>
          (Plus, get the Heart Health Collection — featuring expert interviews, heart-healthy recipes, and actionable
          tools — absolutely FREE when you join today!)
        </p>
        <CheckoutButton className="offer-box-button" sku={productSku} price={productValue} slug={productSlug}>
          <FontAwesomeIcon
            className="mr-2"
            // @ts-ignore
            icon={faCartPlus}
          />{' '}
          {btnText}
        </CheckoutButton>
        <SalesDisclaimer iconColor={iconColor} />
        <p className="mt-4 mb-1">Prefer a 3-month payment plan? We’ve got you covered!</p>
        <CheckoutButton
          color="link"
          className="btn-link-checkout"
          sku={productSku}
          price={productPaymentPlanValue}
          slug={productSlug}
          queryParams={{opt: 1}}
        >
          Join now for just ${productPaymentPlanValue}/month!
        </CheckoutButton>
      </>
    );
  }

  useBalanceText();
  useBeacon();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title="Watch the Healthy Heart Masterclass with Dr. Mimi Guarneri" />
      <div className="page-content">
        <SectionCL id="hhcv1-webinar" color="white" angle="bottom" type="triangle" order="first">
          <Container>
            <Row className="text-center">
              <Col>
                <p>
                  <img
                    className="mb-3"
                    src="https://cdn.foodrevolution.org/hhc/hhc-masterclass-logo.svg"
                    alt="Healthy Heart Masterclass logo"
                    style={{maxHeight: '75px'}}
                  />
                </p>
                <p className="balance-text">
                  In this <b>FREE Masterclass,</b> Dr. Mimi Guarneri reveals 4 heart myths and 10 groundbreaking truths
                  that can help you achieve and maintain a healthy heart.
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center mt-4">
              <Col lg="10" className="video-col px-0 px-sm-3">
                {replayOverride || replayFull ? (
                  <Video
                    url="https://customer-ros8xhsmbkksflna.cloudflarestream.com/6a14bf8fd8c14fa78de753d4dc513848/watch"
                    label="HHC - Masterclass"
                    thumbnail="https://cdn.foodrevolution.org/hhc/videothumb/hhc-masterclass-thumbnail-20210910.jpg"
                  />
                ) : (
                  <WebinarNew noun="Masterclass" label="HHC - Masterclass" />
                )}
                <div className="background-purple p-3 text-center text-white">
                  <p className="balance-text m-0">
                    <b>IMPORTANT:</b> Please try to stay for the whole Masterclass. Taking the time to keep your heart
                    healthy is the most valuable thing you can do today. You’ll be so glad you did!
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="light-gray" type="triangle" className="text-center">
          <Container>
            <Row>
              <Col>
                {beforeWebinar && (
                  <h4 className="section-heading text-400">
                    While we are waiting to get started — <b>here is what you can do.</b>
                  </h4>
                )}
                <h3 className={classNames(`text-purple`, beforeWebinar ? 'mt-5' : 'section-heading')}>
                  Download your FREE Workbook
                </h3>
                <img
                  className="mb-4"
                  src="https://cdn.foodrevolution.org/hhc/hhc-masterclass-workbook.png"
                  alt="healthy heart masterclass workbook cover"
                  style={{maxHeight: '200px'}}
                />
                <p className="balance-text mb-4">
                  Use this workbook to get the MOST out of the <i>Healthy Heart Masterclass.</i>
                </p>
                <p>
                  <Button
                    color="cta"
                    href={workbookDownloadUrl}
                    className=""
                    target="_blank"
                    onClick={() => {
                      track('Button Clicked', {
                        label: 'Download My Workbook',
                        url: workbookDownloadUrl
                      });
                    }}
                  >
                    Download My Workbook
                  </Button>
                </p>
                <h4 className="text-400 mb-0">
                  We are{' '}
                  <b>
                    <i>so excited</i>
                  </b>{' '}
                  you are here. Thank you for taking the time to join us.
                </h4>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id=""
          color="white"
          className="text-center"
          type="triangle"
          angle={delayReveal || replayOverride ? 'bottom' : 'none'}
          order={delayReveal || replayOverride ? undefined : 'last'}
        >
          <Container>
            <Row>
              <Col>
                <img
                  className="mb-4"
                  src="https://cdn.foodrevolution.org/global/frn-logo-2024-trademarked-color.svg"
                  alt="Food Revolution Network logo"
                  width={300}
                  height={126}
                  style={{maxHeight: '200px'}}
                />
                <p className="balance-text">
                  Food Revolution Network is committed to educate and advocate for healthy, ethical, and sustainable
                  food for all.
                </p>
                <p className="balance-text">
                  Guided by John and Ocean Robbins, with more than a million members and with the collaboration of many
                  of the top food revolutionary leaders of our times, Food Revolution Network aims to change the way the
                  world eats.
                </p>
                <p className="balance-text">
                  As a certified B Corp, we commit to these values in every aspect of our work.
                </p>
                <img
                  src="https://cdn.foodrevolution.org/global/b-corp-logo-black.svg"
                  alt="B Corp Logo"
                  width={103}
                  height={150}
                  style={{maxHeight: '200px'}}
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        {delayReveal && (
          <>
            <SectionCL id="" color="purple" className="text-center text-white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h5 className="section-only-heading">
                      As a thank you for attending this Masterclass, you've now unlocked a special 50% enrollment
                      discount for your seat in Mimi’s life-changing course, <i>Healthy Heart.</i>
                    </h5>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="" color="white" type="triangle" innerClass="pb-3" angle="none">
              <Container>
                <Row className="text-center">
                  <Col>
                    <p>
                      <b>
                        <i>This special enrollment discount is ONLY available on this page.</i>
                      </b>
                    </p>
                    <p>
                      Now that you understand what’s possible, it’s time to take charge of your heart health! With
                      simple, proven lifestyle changes, you can enjoy more energy, vibrant health, and a stronger,
                      happier heart for years to come.
                    </p>
                  </Col>
                </Row>
                <Row className="justify-content-center mt-4">
                  <Col lg="10">
                    <div className={classNames(`text-center rounded box-shadow-black-50`, `cta-box-screening`)}>
                      <div className="background-purple text-white rounded-top p-3">
                        <h5 className="section-only-heading">
                          Enroll Today, and You’ll Get Your 50% Discount + Get the Heart Health Collection for FREE!
                        </h5>
                      </div>
                      <div className="background-light-gray rounded-bottom p-4 p-lg-5">
                        <img
                          className="mb-4"
                          src="https://cdn.foodrevolution.org/hhc/hhc-logo.svg"
                          alt="Healthy Heart logo"
                          style={{maxHeight: '100px'}}
                        />
                        <CtaOffer />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SalesCopyV1 version="screening" variant={variant} bucket={bucket}>
              <h3 className="section-heading text-center text-white text-400">
                Take Charge of Your Health with{' '}
                <b>
                  <i>Healthy Heart</i>
                </b>
              </h3>
              <div className="cta-box background-light-gray rounded box-shadow-black-50 text-center p-4 p-lg-5">
                <CtaOffer />
              </div>
              <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
                <Col xs="11" lg="8" className="mb-4 mb-lg-0">
                  <h6 className="section-only-heading text-center text-lg-left text-400">
                    For every new order, we make a donation to Trees for the Future, enabling them to plant another
                    organic fruit or nut tree in a low-income community.
                  </h6>
                </Col>
                <Col xs="11" lg="3" className="text-center">
                  <img
                    className="logo-trees"
                    alt="Trees for the Future logo"
                    src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                    width={1080}
                    height={442}
                    style={{maxHeight: '100px', width: 'auto'}}
                  />
                </Col>
              </Row>
            </SalesCopyV1>
          </>
        )}
      </div>

      {replayOverride ? (
        <SectionCL
          id="replay-cta"
          color="gradient-indigo-purple-right"
          angle="none"
          order="last"
          innerClass={classNames(`text-center text-white section-webinar-inner-cta`)}
        >
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">
                  Are you interested in enrolling in Dr. Mimi Guarneri’s <em>Healthy Heart</em> course?
                </h4>
                <h5>Click below to learn more:</h5>
                <Button className="btn-join btn-lg" color="cta" href="/join/" target="_blank">
                  Join Now!
                </Button>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      ) : (
        <>
          {!registrationModalDisabled && <RegistrationModal />}
          <ModalExitScreening />
        </>
      )}

      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
